import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import moment, { Moment } from 'moment';
import styles from './DatePicker.module.scss';
import { GeneratedDatePicker, getDOMElements } from './DatePicker.service';
import { Button, Icon } from 'components/controls';
import { Drawer } from 'components/Drawer/Drawer';
import 'moment/locale/ru';

// For isRangePicker
type RangeDate = [Moment | null, Moment | null] | undefined;
type DefaultDate = Moment | undefined;
type Date = RangeDate | DefaultDate;

interface DatePickerProps {
    onClose: () => void;
    onApply: (date: Date) => void;
    isVisible?: boolean;
    isRangePicker?: boolean;
}

const pickerPlaceholder: [string, string] = ['с', 'по'];

const defaultPickerValue: [Moment, Moment] = [
    moment().add(-1, 'month'),
    moment(),
];

/**
 * @param {DatePickerProps} props - Props of the Component.
 * @description Custom date picker component.
 * @returns {ReactElement} DatePicker element.
 */
export const DatePicker: React.FC<DatePickerProps> = (props) => {
    const [parentNode, setParentNode] = useState<Element>();
    const [date, setDate] = useState<Date>();

    const handleApply = useCallback(() => {
        props.onApply(date);
    }, [date, props]);

    const handleCalendarChange = useCallback(
        (newDate) => {
            const resetTime = {
                hour: 0,
                minute: 0,
                second: 0,
                millisecond: 0,
            };

            if (props.isRangePicker) {
                setDate(() => [
                    newDate?.[0] ? newDate[0].set(resetTime) : null,
                    newDate?.[1] ? newDate[1].set(resetTime) : null,
                ]);
            } else {
                setDate(() => newDate?.set(resetTime) as Date);
            }
        },
        [props.isRangePicker],
    );

    const getPopupContainer = useCallback(
        () => parentNode as HTMLElement,
        [parentNode],
    );

    const getDisabledDate = useCallback(
        (current) => current && current > moment().endOf('day'),
        [],
    );

    const isApplyDisabled = props.isRangePicker && (!date?.[0] || !date?.[1]);

    useEffect(() => {
        const node = document.getElementsByClassName(styles.drawer)[0];

        if (node) {
            setParentNode(
                () => node.getElementsByClassName('ant-drawer-body')[0],
            );
        }
    }, [props.isVisible]);

    useEffect(() => {
        if (parentNode) {
            const [
                headerView1,
                headerView2,
                fromDateInput,
                toDateInput,
                clearFromButton,
                clearToButton,
            ] = getDOMElements(parentNode, styles);

            headerView1.onclick = (e) => {
                e.stopPropagation();
            };

            if (props.isRangePicker) {
                headerView2.onclick = (e) => {
                    e.stopPropagation();
                };
            }

            clearFromButton.onclick = () =>
                setDate((prev) => (prev ? [null, prev[1]] : undefined));

            if (props.isRangePicker) {
                clearToButton.onclick = () =>
                    setDate((prev) => (prev ? [prev[0], null] : undefined));
            }

            fromDateInput.append(clearFromButton);

            if (props.isRangePicker) {
                toDateInput.append(clearToButton);
            }
        }
    }, [parentNode, props.isRangePicker]);

    const title = props.isRangePicker ? 'Выберите период' : 'Выберите дату';

    const drawerClassName = clsx(styles.drawer, {
        [styles.isNotRangePicker]: !props.isRangePicker,
    });

    return (
        <Drawer
            type='datePicker'
            placement='bottom'
            height='100%'
            onClose={props.onClose}
            open={props.isVisible}
            className={drawerClassName}
            closeIcon={<Icon type='navigationClose' size={24} />}
        >
            <div className={styles.title}>{title}</div>

            {parentNode &&
                (props.isRangePicker ? (
                    <GeneratedDatePicker.RangePicker
                        placeholder={pickerPlaceholder}
                        format='D MMM YYYY'
                        onCalendarChange={handleCalendarChange}
                        value={date as RangeDate}
                        inputReadOnly
                        disabledDate={getDisabledDate}
                        defaultPickerValue={defaultPickerValue}
                        getPopupContainer={getPopupContainer}
                        open={props.isVisible}
                        separator={
                            <Icon
                                type='actionsNavigationArrowRight'
                                size={24}
                            />
                        }
                        dropdownClassName={styles.datepickerDropDown}
                    />
                ) : (
                    <GeneratedDatePicker
                        open={props.isVisible}
                        disabledDate={getDisabledDate}
                        getPopupContainer={getPopupContainer}
                        dropdownClassName={styles.datepickerDropDown}
                        onChange={handleCalendarChange}
                        showToday={false}
                    />
                ))}

            <div className={styles.button}>
                <Button
                    isDisabled={isApplyDisabled}
                    onClick={handleApply}
                    title='Готово'
                />
            </div>
        </Drawer>
    );
};
